import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ChatFlightFareTicket,
  ChatFlightResultCard,
  RuleActionExtension
} from '@sabstravtech/obtservices/base';
import { ResultAwareService } from '@sabstravtech/obtservices/angular';
import { formatCardCurrency, policyStatusClass, policyStatusLabel } from '../chat-card.util';

/** Flight-number tokens used to deep-link the results page to the flight a
 *  chat fare belongs to. `marketingCarrier + marketingFlightNumber` of the
 *  first segment of each leg (e.g. "BA1466"); the results-page filter matches
 *  any itinerary containing that segment. Either side may be empty. */
export interface ChatFlightFareSelection {
  outboundFlightNumber: string;
  inboundFlightNumber: string;
}

type ChatFlightCardData = ChatFlightResultCard & {
  policyMessages?: string[];
};

@Component({
  selector: 'app-chat-flight-card',
  standalone: false,
  templateUrl: './chat-flight-card.component.html',
  styleUrls: ['./chat-flight-card.component.scss']
})
export class ChatFlightCardComponent {
  @Input() card!: ChatFlightCardData;
  /** ENT-16247 — id of the assistant message these results belong to. */
  @Input() feedbackBaseId = '';
  /** True when this card represents a separately-priced inbound (dual-single)
   *  leg, so its flight number must be applied to the inbound filter. */
  @Input() isInbound = false;
  /** Emitted when the user picks a fare — the parent navigates to the flight
   *  results page filtered to this flight. */
  @Output() fareSelect = new EventEmitter<ChatFlightFareSelection>();

  readonly fareOptionsLabel = $localize`:@@chatFlightFareOptionsAria:View fare options for`;

  isExpanded = false;

  constructor(private resultAwareService: ResultAwareService) {}

  /** ENT-16247 — synthetic feedback id, unique per card. */
  get feedbackId(): string {
    return `${this.feedbackBaseId || 'card'}::flight-${this.card.index}`;
  }

  /** ENT-16247 — short card summary sent as the feedback assistantResponse. */
  get feedbackSummary(): string {
    return `Flight ${this.card.airline} ${this.card.cabin}, from ${this.card.fromPrice} ${this.card.currency}`;
  }

  get hasFareOptions(): boolean {
    return !!this.card.fares && this.card.fares.tickets.length > 0;
  }

  toggleExpand(): void {
    if (this.isBlocked) return;
    // Don't gate on `hasFareOptions` — even when the supplier returned a
    // single fare brand the user expects clicking to do *something* (mirrors
    // the rail card UX). The template renders an explicit "no fare options"
    // state inside the expanded panel when there are none to show.
    this.isExpanded = !this.isExpanded;
  }

  /** Open the OBT "Fare Rules" conditions dialog for this fare, mirroring the
   *  results page (and the rail card's "View Conditions"). */
  onFareRules(ticket: ChatFlightFareTicket | undefined, event: Event): void {
    event.stopPropagation();
    if (!ticket) return;
    this.resultAwareService.openFlightFareDetails(ticket);
  }

  /** Add this specific fare to the basket (mirrors the rail card's "Select"). */
  onSelectFareTicket(ticket: ChatFlightFareTicket | undefined, event: Event): void {
    event.stopPropagation();
    if (!ticket || ticket.unavailable) return;
    this.resultAwareService.selectFlightFare(ticket).catch((error) => {
      console.error('[ChatFlightCard] Flight fare selection failed:', error);
    });
  }

  /** Deep-link to this flight on the results page (mirrors the hotel card's
   *  "click hotel → filtered results" behaviour). The results page filters by a
   *  single segment's flight number, so we send the first segment of each leg.
   *  For a dual-single inbound card the flight is an inbound leg, so it goes on
   *  the inbound side of the filter. */
  onSelectFare(event: Event): void {
    event.stopPropagation();
    if (this.isBlocked) return;
    const firstSegment = (flightNumbers: string | undefined): string =>
      (flightNumbers?.split('/')[0] ?? '').trim();
    const ownNumber = firstSegment(this.card.outbound?.flightNumbers);
    const selection: ChatFlightFareSelection = this.isInbound
      ? { outboundFlightNumber: '', inboundFlightNumber: ownNumber }
      : {
          outboundFlightNumber: ownNumber,
          inboundFlightNumber: firstSegment(this.card.inbound?.flightNumbers)
        };
    this.fareSelect.emit(selection);
  }

  get formattedPrice(): string {
    return this.formatCurrency(this.card.fromPrice, this.card.currency);
  }

  get isBlocked(): boolean {
    return this.card.policyStatus === 'UNAVAILABLE';
  }

  get policyClass(): string {
    return policyStatusClass(this.card.policyStatus);
  }

  get policyLabel(): string {
    return policyStatusLabel(this.policyClass);
  }

  /** Headline fare's policy fields, shaped for `app-warning-tags` — the same
   *  indicator/tooltip component the results page uses, so the chat card and
   *  results page render identically instead of a bespoke text box. */
  get headlineWarningTicket(): RuleActionExtension {
    return {
      unavailable: !!this.card.unavailable,
      unavailableMessage: this.card.unavailableMessage || '',
      requiresReasonKeys: this.card.requiresReasonKeys || [],
      requiresReasonMessages: this.card.requiresReasonMessages || []
    };
  }

  /** Per-fare policy fields for the expanded "Available fares" list, shaped
   *  for `app-warning-tags` — a fare option can be out of policy independently
   *  of the headline card's cheapest fare. */
  warningTicketFor(ticket: ChatFlightFareTicket): RuleActionExtension {
    return {
      unavailable: !!ticket.unavailable,
      unavailableMessage: ticket.unavailableMessage || '',
      requiresReasonKeys: ticket.requiresReasonKeys || [],
      requiresReasonMessages: ticket.requiresReasonMessages || []
    };
  }

  get airlineLogoUrl(): string | null {
    return this.card.airlineCode
      ? `https://images.kiwi.com/airlines/64/${this.card.airlineCode.toUpperCase()}.png`
      : null;
  }

  stopsLabel(stops: number): string {
    if (stops === 0) return $localize`:@@chatFlightDirect:Direct`;
    if (stops === 1) return $localize`:@@chatFlightOneStop:1 Stop`;
    return $localize`:@@chatFlightManyStops:${stops}:stops:+ Stops`;
  }

  onClick(): void {
    this.toggleExpand();
  }

  onKey(event: Event): void {
    event.preventDefault();
    this.toggleExpand();
  }

  formatCurrency(amount: number | undefined, currency = 'GBP'): string {
    return formatCardCurrency(amount, currency);
  }
}
